import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import charterPrepWsInitialState, {
  type CharterPrepWsState,
} from '../initialState/charterPrepWsInitialState'
import type { PrepChatMessage } from '../types/charterWsTypes'

const STREAMING_MESSAGE_ID_PREFIX = 'stream_'

const charterPrepWsSlice = createSlice({
  name: 'charterPrepWs',
  initialState: charterPrepWsInitialState as CharterPrepWsState,
  reducers: {
    setConnected(state, action: PayloadAction<boolean>) {
      state.isConnected = action.payload
      if (!action.payload) {
        state.isStreaming = false
        state.currentStreamId = null
      }
    },

    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload
    },

    // Called when chat_history arrives on connect
    setChatHistory(
      state,
      action: PayloadAction<{
        messages: PrepChatMessage[]
        completed_sections: Record<string, string>
        active_section_key: string | null
        profile: Record<string, unknown> | null
        sharing_mode: string
        visible_sections: string[]
        section_order: string[]
        status: string | null
        profile_approved: boolean
      }>
    ) {
      const p = action.payload
      state.messages = p.messages
      state.completedSections = p.completed_sections
      state.activeSectionKey = p.active_section_key
      state.profile = p.profile
      state.sharingMode = p.sharing_mode
      state.visibleSections = p.visible_sections
      state.sectionOrder = p.section_order
      state.status = p.status
      state.profileApproved = p.profile_approved ?? false
      // If already approved, no approval prompt needed
      if (p.profile_approved) {
        state.profileApprovalRequired = false
        state.sharingModeSelected = true
      }
    },

    // Called on chat_message_chunk
    appendOrUpdateStreamChunk(
      state,
      action: PayloadAction<{ stream_id: string; content: string }>
    ) {
      const { stream_id, content } = action.payload
      const tempId = STREAMING_MESSAGE_ID_PREFIX + stream_id

      const existing = state.messages.find((m) => m.id === tempId)
      if (existing) {
        existing.content += content
      } else {
        state.messages.push({
          id: tempId,
          role: 'assistant',
          content,
          is_streaming: true,
          created_at: new Date().toISOString(),
        })
        state.isStreaming = true
        state.currentStreamId = stream_id
      }
    },

    // Called on chat_message_final
    finalizeStreamMessage(
      state,
      action: PayloadAction<{
        stream_id: string
        content: string
        section_key?: string
      }>
    ) {
      const { stream_id, content, section_key } = action.payload
      const tempId = STREAMING_MESSAGE_ID_PREFIX + stream_id

      const idx = state.messages.findIndex((m) => m.id === tempId)
      if (idx !== -1) {
        state.messages[idx] = {
          ...state.messages[idx],
          id: `final_${stream_id}`,
          content,
          section_key,
          is_streaming: false,
        }
      }
      state.isStreaming = false
      state.currentStreamId = null
    },

    appendUserMessage(
      state,
      action: PayloadAction<{ content: string; section_key?: string }>
    ) {
      state.messages.push({
        id: `user_${Date.now()}`,
        role: 'user',
        content: action.payload.content,
        section_key: action.payload.section_key,
        is_streaming: false,
        created_at: new Date().toISOString(),
      })
    },

    markSectionComplete(
      state,
      action: PayloadAction<{ section_key: string; completed_sections: Record<string, string> }>
    ) {
      state.completedSections = action.payload.completed_sections
    },

    setActiveSectionKey(state, action: PayloadAction<string | null>) {
      state.activeSectionKey = action.payload
    },

    setQualityCheckFailed(
      state,
      action: PayloadAction<{
        section_key: string
        trigger_type: string | null
        coaching_hint: string | null
      } | null>
    ) {
      state.qualityCheckFailed = action.payload
    },

    setProfileApprovalRequired(
      state,
      action: PayloadAction<{ profile: Record<string, unknown> }>
    ) {
      state.profileApprovalRequired = true
      state.isGeneratingProfile = false
      state.profile = action.payload.profile
    },

    setIsGeneratingProfile(state, action: PayloadAction<boolean>) {
      state.isGeneratingProfile = action.payload
    },

    markProfileApproved(state) {
      state.profileApproved = true
    },

    setSharingMode(state, action: PayloadAction<string>) {
      state.sharingMode = action.payload
      state.sharingModeSelected = true
    },

    setVisibleSections(state, action: PayloadAction<string[]>) {
      state.visibleSections = action.payload
    },

    setStatus(state, action: PayloadAction<string | null>) {
      state.status = action.payload
    },

    setMessages(state, action: PayloadAction<PrepChatMessage[]>) {
      if (state.messages.length === 0) {
        state.messages = action.payload
      }
    },
  },
})

export const {
  setConnected,
  setError,
  setChatHistory,
  appendOrUpdateStreamChunk,
  finalizeStreamMessage,
  appendUserMessage,
  markSectionComplete,
  setActiveSectionKey,
  setQualityCheckFailed,
  setProfileApprovalRequired,
  setIsGeneratingProfile,
  markProfileApproved,
  setSharingMode,
  setVisibleSections,
  setStatus,
  setMessages,
} = charterPrepWsSlice.actions

export default charterPrepWsSlice.reducer
